import React, { useMemo } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { type Theme, alpha } from '@mui/material/styles';

import type { LensCard, LensValueFormat } from '~/types/leLens';

import { fieldFormat, formatLensLabel, formatLensValue, isNumeric } from '../lensChart';

import { wandTargetAttribute } from './wandGeometry';

type Shape = { rows?: Array<Record<string, unknown>>; columns?: Array<string> };

export type LensDriverRow = {
  label: string;
  change: number | null;
  changePct: number | null;
  currentValue?: number | null;
  priorValue?: number | null;
};

type MissingDriverRow = {
  label: string;
  change: null;
  changePct: null;
  currentValue: number | null;
  priorValue: number | null;
};

/** A missing custom-query group is unknown, not an implicit zero. Keep it visible. */
export function lensDriverDisplayRows(
  current: Shape,
  prior: Shape,
  card: LensCard,
): Array<LensDriverRow | MissingDriverRow> {
  const matched = lensDriverRows(current, prior, card);
  const columns = current.columns ?? prior.columns ?? [];
  const labelKey = card.view?.x ?? columns.find((column) => column !== card.view?.value);
  const valueKey = card.view?.value ?? columns.find((column) => column !== labelKey);
  if (!labelKey || !valueKey) return matched;
  const byLabel = (shape: Shape) =>
    new Map((shape.rows ?? []).map((row) => [String(row[labelKey] ?? ''), row[valueKey]]));
  const currentByLabel = byLabel(current);
  const priorByLabel = byLabel(prior);
  const matchedLabels = new Set(matched.map((row) => row.label));
  const missing: Array<MissingDriverRow> = [...new Set([...currentByLabel.keys(), ...priorByLabel.keys()])]
    .filter((label) => !matchedLabels.has(label))
    .map((label) => ({
      label,
      change: null,
      changePct: null,
      currentValue: isNumeric(currentByLabel.get(label)) ? Number(currentByLabel.get(label)) : null,
      priorValue: isNumeric(priorByLabel.get(label)) ? Number(priorByLabel.get(label)) : null,
    }));
  return [...matched, ...missing].sort(
    (left, right) =>
      Math.abs(right.change ?? Math.max(Math.abs(right.currentValue ?? 0), Math.abs(right.priorValue ?? 0))) -
      Math.abs(left.change ?? Math.max(Math.abs(left.currentValue ?? 0), Math.abs(left.priorValue ?? 0))),
  );
}

export function lensDriverRows(current: Shape, prior: Shape, card: LensCard): Array<LensDriverRow> {
  const columns = current.columns ?? [];
  const labelKey = card.view?.x ?? columns.find((column) => column !== card.view?.value);
  const valueKey =
    card.view?.value ?? columns.find((column) => (current.rows ?? []).some((row) => isNumeric(row[column])));
  if (!labelKey || !valueKey) return [];

  const priorByLabel = new Map(
    (prior.rows ?? []).map((row) => [
      String(row[labelKey] ?? ''),
      isNumeric(row[valueKey]) ? Number(row[valueKey]) : null,
    ]),
  );
  return (current.rows ?? [])
    .flatMap((row) => {
      const label = String(row[labelKey] ?? '');
      const currentValue = isNumeric(row[valueKey]) ? Number(row[valueKey]) : null;
      const priorValue = priorByLabel.get(label);
      if (currentValue == null || priorValue == null) return [];
      const change = currentValue - priorValue;
      return [{ label, change, changePct: priorValue === 0 ? null : change / Math.abs(priorValue) }];
    })
    .sort((left, right) => Math.abs(right.change ?? 0) - Math.abs(left.change ?? 0));
}

export function signedChange(value: number, format: LensValueFormat | undefined): string {
  if (format?.kind === 'percent') {
    const sign = value >= 0 ? '+' : '';
    const percentagePoints = value * (format.percentScale === 'percent' ? 1 : 100);
    return `${sign}${percentagePoints.toFixed(1)}pp`;
  }
  const formatted = formatLensValue(value, format);
  return value > 0 ? `+${formatted}` : formatted;
}

function percentMovement(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return 'Unavailable';
  return `${value >= 0 ? '↑' : '↓'} ${Math.abs(value * 100).toFixed(Math.abs(value) < 0.1 ? 2 : 1)}%`;
}

const PRODUCT_LABELS: Record<string, string> = {
  bedbank: 'Bedbank',
  custom_offer_cruise: 'Cruise',
  custom_offer_experience: 'Experiences',
  custom_offer_flight: 'Flights',
  custom_offer_other: 'Other',
  hotel: 'Accom (incl. NHA)',
  tour: 'Tours',
};

function driverLabel(value: string, card: LensCard): string {
  if (card.view?.x === 'product_bk' && PRODUCT_LABELS[value]) return PRODUCT_LABELS[value];
  const formatted = formatLensLabel(value, card.config?.labelFormat);
  return formatted.includes('_')
    ? formatted.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase())
    : formatted;
}

export default function LensDriversCard({
  card,
  comparison,
  comparisonLabel,
  current,
  format: formatOverride,
  loadingComparison,
  onSelect,
  plotHeight,
  theme,
}: {
  card: LensCard;
  current: Shape;
  format?: LensValueFormat;
  comparison?: Shape;
  comparisonLabel?: string;
  loadingComparison: boolean;
  onSelect: (pick: { column: string; value: string }) => void;
  plotHeight: number;
  theme: Theme;
}) {
  const rows = useMemo(
    () => (comparison ? lensDriverDisplayRows(current, comparison, card) : []),
    [card, comparison, current],
  );
  const labelKey = card.view?.x ?? current.columns?.find((column) => column !== card.view?.value);
  const valueKey = card.view?.value ?? current.columns?.find((column) => column !== labelKey);
  const format = formatOverride ?? (valueKey ? fieldFormat(card.config, valueKey) : undefined);
  const visibleCount = Math.max(4, Math.min(10, Math.floor((plotHeight - 42) / 34)));
  const visibleRows = rows.slice(0, visibleCount);
  const maxChange = Math.max(...visibleRows.map((row) => Math.abs(row.change ?? 0)), 1e-9);
  const hasMissing = rows.some((row) => row.change == null);
  const netChange = rows.reduce((total, row) => total + (row.change ?? 0), 0);
  const columns = 'minmax(96px, 180px) minmax(96px, 1fr) minmax(76px, 108px) minmax(72px, 92px)';

  if (loadingComparison) {
    return <Typography color="text.secondary">Loading {comparisonLabel?.toLowerCase() ?? 'comparison'}…</Typography>;
  }
  if (!comparison) {
    return <Typography color="text.secondary">Choose a comparison period to see the drivers.</Typography>;
  }
  if (!visibleRows.length) {
    return <Typography color="text.secondary">No groups were returned for either period.</Typography>;
  }

  return (
    <Box sx={{ alignSelf: 'stretch', overflowY: 'auto', width: '100%' }}>
      <Box aria-hidden sx={{ columnGap: 1.25, display: 'grid', gridTemplateColumns: columns, mb: 0.75 }}>
        <Box />
        <Box />
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 10,
            fontWeight: 700,
            textAlign: 'right',
            textTransform: 'uppercase',
          }}
        >
          Change
        </Typography>
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 10,
            fontWeight: 700,
            textAlign: 'right',
            textTransform: 'uppercase',
          }}
        >
          Change %
        </Typography>
      </Box>
      <Stack spacing={0.8} role="list">
        {visibleRows.map((row) => {
          if (row.change == null) {
            const label = driverLabel(row.label, card);
            return (
              <Box
                key={row.label}
                role="listitem"
                sx={{ columnGap: 1.25, display: 'grid', gridTemplateColumns: columns }}
              >
                <Typography noWrap sx={{ fontSize: 12.5, textAlign: 'right' }} title={row.label}>
                  {label}
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: 12, gridColumn: 'span 3' }}>
                  Current: {row.currentValue == null ? 'No result' : formatLensValue(row.currentValue, format)}
                  {' · '}Prior: {row.priorValue == null ? 'No result' : formatLensValue(row.priorValue, format)}
                  {' · '}Change unavailable
                </Typography>
              </Box>
            );
          }
          const positive = row.change >= 0;
          const colour = positive ? theme.palette.success.main : theme.palette.error.main;
          const width = `${Math.max(1, (Math.abs(row.change) / maxChange) * 50)}%`;
          const label = driverLabel(row.label, card);
          return (
            <Box
              aria-label={`${label}: ${signedChange(row.change, format)}, ${percentMovement(row.changePct)}`}
              data-lens-wand={wandTargetAttribute({
                kind: 'driver',
                label,
                value: `${signedChange(row.change, format)} (${percentMovement(row.changePct)})`,
              })}
              key={row.label}
              onClick={() => labelKey && onSelect({ column: labelKey, value: row.label })}
              role="listitem"
              sx={{
                alignItems: 'center',
                columnGap: 1.25,
                cursor: labelKey ? 'pointer' : 'default',
                display: 'grid',
                gridTemplateColumns: columns,
              }}
            >
              <Typography noWrap sx={{ fontSize: 12.5, textAlign: 'right' }} title={row.label}>
                {label}
              </Typography>
              <Box sx={{ height: 11, minWidth: 80, position: 'relative' }}>
                {/* MUI treats a numeric width of 1 as 100%, so the zero line needs an explicit pixel unit. */}
                <Box
                  sx={{
                    backgroundColor: alpha(theme.palette.text.primary, 0.16),
                    bottom: -3,
                    left: '50%',
                    position: 'absolute',
                    top: -3,
                    width: '1px',
                  }}
                />
                <Box
                  sx={{
                    backgroundColor: colour,
                    borderRadius: '8px',
                    height: 11,
                    position: 'absolute',
                    top: 0,
                    width,
                    ...(positive ? { left: '50%' } : { right: '50%' }),
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: colour,
                  fontSize: 12.5,
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                }}
              >
                {signedChange(row.change, format)}
              </Typography>
              <Typography
                sx={{
                  color: colour,
                  fontSize: 12.5,
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                }}
              >
                {percentMovement(row.changePct)}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      <Typography color="text.secondary" sx={{ fontSize: 12, mt: 1 }}>
        {hasMissing
          ? 'Total change is unavailable because some groups have no result in one period.'
          : `Total change: ${signedChange(netChange, format)}.`}
        {rows.length > visibleRows.length ? ` Showing ${visibleRows.length} of ${rows.length} groups.` : ''}
      </Typography>
    </Box>
  );
}
